import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckSquare,
  Square,
  Link as LinkIcon,
  Paperclip,
  User,
  Calendar,
  AlertCircle,
  X,
  Filter
} from 'lucide-react';
import { Task, BoardMember } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';

interface GlobalTaskViewProps {
  isOpen: boolean;
  onClose: () => void;
  boardMembers: BoardMember[];
}

const GlobalTaskView: React.FC<GlobalTaskViewProps> = ({ 
  isOpen, 
  onClose, 
  boardMembers 
}) => {
  const { user } = useAuth();
  useNotifications();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [filteredTasks, setFilteredTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<'all' | 'assigned' | 'created'>('assigned');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Load tasks when component opens
  useEffect(() => {
    if (isOpen && user) {
      loadTasks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTasks is stable
  }, [isOpen, user]);

  // Filter tasks when filters change
  useEffect(() => {
    let filtered = tasks;

    // Filter by assignment
    if (filter === 'assigned') {
      filtered = filtered.filter(task => task.owner === user?.id);
    } else if (filter === 'created') {
      filtered = filtered.filter(task => task.createdBy === user?.id);
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(task => task.status === statusFilter);
    }

    setFilteredTasks(filtered);
  }, [tasks, filter, statusFilter, user?.id]);

  const loadTasks = async () => {
    setIsLoading(true);
    try {
      // TODO: Implement getTasksForUser in firestore
      // For now, using mock data
      const mockTasks: Task[] = [
        {
          id: '1',
          productId: 'product1',
          boardId: 'board1',
          title: 'Market Research',
          description: 'Conduct competitor analysis',
          status: 'In Progress',
          priority: 'High',
          owner: user?.id || '',
          ownerEmail: user?.email || '',
          createdBy: user?.id || '',
          createdByEmail: user?.email || '',
          createdAt: new Date(),
          updatedAt: new Date(),
          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        },
        {
          id: '2',
          productId: 'product2',
          boardId: 'board1',
          title: 'User Testing',
          description: 'Conduct user interviews',
          status: 'Not Started',
          priority: 'Medium',
          owner: user?.id || '',
          ownerEmail: user?.email || '',
          createdBy: 'other-user',
          createdByEmail: 'other@example.com',
          createdAt: new Date(),
          updatedAt: new Date(),
          dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000)
        }
      ];
      setTasks(mockTasks);
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateTask = async (taskId: string, updates: Partial<Task>) => {
    try {
      // TODO: Implement updateTask in firestore
      setTasks(tasks.map(task => 
        task.id === taskId 
          ? { ...task, ...updates, updatedAt: new Date() }
          : task
      ));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckSquare size={16} className="text-green-600" />;
      case 'In Progress':
        return <AlertCircle size={16} className="text-blue-600" />;
      case 'Blocked':
        return <AlertCircle size={16} className="text-red-600" />;
      case 'On Hold':
        return <AlertCircle size={16} className="text-yellow-600" />;
      default:
        return <Square size={16} className="text-gray-400" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'text-red-600 bg-red-100';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'Low':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString();
  };

  const isOverdue = (dueDate: Date) => {
    return new Date(dueDate) < new Date();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed':
        return 'text-green-600 bg-green-100';
      case 'In Progress':
        return 'text-blue-600 bg-blue-100';
      case 'Blocked':
        return 'text-red-600 bg-red-100';
      case 'On Hold':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-3xl p-6 w-full max-w-4xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <CheckSquare size={24} className="text-satsuma-600" />
                <h2 className="text-2xl font-bold text-gray-800">My Tasks</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Filters */}
            <div className="flex items-center space-x-4 mb-6 p-4 bg-white/30 rounded-lg">
              <div className="flex items-center space-x-2">
                <Filter size={16} className="text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Filter:</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setFilter('assigned')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filter === 'assigned' 
                      ? 'bg-satsuma-500 text-white' 
                      : 'bg-white/50 text-gray-600 hover:bg-white/70'
                  }`}
                >
                  Assigned to Me
                </button>
                <button
                  onClick={() => setFilter('created')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filter === 'created' 
                      ? 'bg-satsuma-500 text-white' 
                      : 'bg-white/50 text-gray-600 hover:bg-white/70'
                  }`}
                >
                  Created by Me
                </button>
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filter === 'all' 
                      ? 'bg-satsuma-500 text-white' 
                      : 'bg-white/50 text-gray-600 hover:bg-white/70'
                  }`}
                >
                  All Tasks
                </button>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm bg-white/50"
              >
                <option value="all">All Status</option>
                <option value="Not Started">Not Started</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Blocked">Blocked</option>
                <option value="On Hold">On Hold</option>
              </select>
            </div>

            {/* Task List */}
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="text-center py-8">
                  <div className="w-6 h-6 border-2 border-satsuma-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-gray-500">Loading tasks...</p>
                </div>
              ) : filteredTasks.length === 0 ? (
                <div className="text-center py-8">
                  <CheckSquare size={48} className="text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No tasks found</p>
                  <p className="text-sm text-gray-400">Try adjusting your filters</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredTasks.map((task) => (
                    <motion.div
                      key={task.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white/30 rounded-xl p-4 border border-white/20"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3 flex-1">
                          {getStatusIcon(task.status)}
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-800">{task.title}</h3>
                            {task.description && (
                              <p className="text-sm text-gray-600 mt-1">{task.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                            {task.status}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <div className="flex items-center space-x-4">
                          {task.owner && (
                            <div className="flex items-center space-x-1">
                              <User size={14} />
                              <span>
                                {boardMembers.find(m => m.userId === task.owner)?.name || 
                                 boardMembers.find(m => m.userId === task.owner)?.email || 
                                 'Unknown'}
                              </span>
                            </div>
                          )}
                          {task.dueDate && (
                            <div className={`flex items-center space-x-1 ${isOverdue(task.dueDate) ? 'text-red-500' : ''}`}>
                              <Calendar size={14} />
                              <span>{formatDate(task.dueDate)}</span>
                              {isOverdue(task.dueDate) && <span className="text-xs">(Overdue)</span>}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          {task.link && (
                            <a
                              href={task.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:text-blue-700"
                            >
                              <LinkIcon size={14} />
                            </a>
                          )}
                          {task.attachment && (
                            <a
                              href={task.attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-gray-500 hover:text-gray-700"
                            >
                              <Paperclip size={14} />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Quick Status Update */}
                      <div className="mt-3 pt-3 border-t border-white/20">
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Quick update:</span>
                          <select
                            value={task.status}
                            onChange={(e) => handleUpdateTask(task.id, { status: e.target.value as any })}
                            className="px-2 py-1 border border-gray-300 rounded text-xs bg-white/50"
                          >
                            <option value="Not Started">Not Started</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                            <option value="Blocked">Blocked</option>
                            <option value="On Hold">On Hold</option>
                          </select>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="mt-6 pt-4 border-t border-white/20">
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Showing {filteredTasks.length} of {tasks.length} tasks</span>
                <div className="flex items-center space-x-4">
                  <span className="flex items-center space-x-1">
                    <CheckSquare size={14} className="text-green-600" />
                    <span>{filteredTasks.filter(t => t.status === 'Completed').length} completed</span>
                  </span>
                  <span className="flex items-center space-x-1">
                    <AlertCircle size={14} className="text-blue-600" />
                    <span>{filteredTasks.filter(t => t.status === 'In Progress').length} in progress</span>
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GlobalTaskView;
